import { useState, useEffect, useCallback, useRef } from "react";
import { UploadSimple, Trash, FilePdf, FileAudio, FileVideo, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { api, errMsg } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (id) => `${BACKEND_URL}/api/files/${id}/content`;
const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const TypeIcon = ({ ct }) => {
  if (ct.startsWith("video/")) return <FileVideo size={32} color="var(--glow)" weight="duotone" />;
  if (ct.startsWith("audio/")) return <FileAudio size={32} color="var(--coral)" weight="duotone" />;
  return <FilePdf size={32} color="var(--muted)" weight="duotone" />;
};

export default function MediaLibrary() {
  useSeo("Media Library", "Upload and keep your thumbnails, b-roll and channel assets in one place.");
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get("/files").then((res) => setFiles(res.data.slice().reverse())), []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("Max file size is 20 MB");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post("/upload", form);
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/files/${id}`);
    toast.success("File removed");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10 lg:py-14" data-testid="media-library-page">
      <div className="fade-up mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--coral)] font-semibold mb-2">Your assets, one place</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">Media Library</h1>
        </div>
        <input ref={inputRef} type="file" accept="image/*,video/mp4,video/quicktime,audio/mpeg,audio/wav,application/pdf" onChange={onPick} className="hidden" data-testid="media-file-input" />
        <button
          data-testid="media-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--glow)] text-white text-sm font-semibold hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
        >
          <UploadSimple size={15} weight="bold" className={uploading ? "animate-bounce" : ""} />
          {uploading ? "Uploading..." : "Upload file"}
        </button>
      </div>

      {files && files.length === 0 && (
        <div className="surface rounded-[20px] p-10 text-center fade-up-1" data-testid="media-empty">
          <FolderOpen size={36} color="var(--muted)" weight="duotone" className="mx-auto" />
          <p className="font-bold mt-4">No media yet</p>
          <p className="text-[var(--muted)] text-sm mt-1.5 max-w-sm mx-auto">
            Upload thumbnails, b-roll clips, audio or PDFs (up to 20 MB) and grab shareable links anytime.
          </p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {files?.map((f, i) => (
          <div key={f.id} className={`surface rounded-[20px] overflow-hidden lift group fade-up-${Math.min(i, 3)}`} data-testid={`media-card-${f.id}`}>
            <div className="aspect-video bg-[var(--bg)] flex items-center justify-center overflow-hidden">
              {f.contentType.startsWith("image/") ? (
                <img src={fileUrl(f.id)} alt={f.filename} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <TypeIcon ct={f.contentType} />
              )}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate" title={f.filename}>{f.filename}</p>
                  <p className="text-xs text-[var(--muted)] mt-0.5">{fmtSize(f.size)} · {dayjs(f.createdAt).format("MMM D")}</p>
                </div>
                <button
                  data-testid={`media-delete-${f.id}`}
                  onClick={() => remove(f.id)}
                  className="opacity-70 lg:opacity-0 lg:group-hover:opacity-100 text-[var(--muted)] hover:text-[var(--coral)] transition-opacity shrink-0"
                >
                  <Trash size={16} />
                </button>
              </div>
              <div className="flex gap-2 mt-3">
                <CopyButton text={fileUrl(f.id)} label="Copy link" testId={`media-copy-${f.id}`} className="!px-3 !py-1.5 !text-xs" />
                <a
                  href={fileUrl(f.id)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`media-open-${f.id}`}
                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-[var(--coral)] text-white text-xs font-semibold hover:opacity-90 transition-opacity"
                >
                  Open
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
